import React, { useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import { services } from "../mock";

const SectorServices = () => {
  const [expandedSector, setExpandedSector] = useState(1);

  const toggleSector = (id) => {
    setExpandedSector(expandedSector === id ? null : id);
  };

  const sectorColors = {
    1: { border: "border-cyan-500/50", gradient: "from-cyan-500/10 to-transparent", text: "text-cyan-400", bg: "bg-cyan-500/20" },
    2: { border: "border-teal-500/50", gradient: "from-teal-500/10 to-transparent", text: "text-teal-400", bg: "bg-teal-500/20" },
    3: { border: "border-emerald-500/50", gradient: "from-emerald-500/10 to-transparent", text: "text-emerald-400", bg: "bg-emerald-500/20" }
  };

  return (
    <section id="services" className="py-24 bg-slate-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Sector-Specific <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">AI Solutions</span>
          </h2>
          <p className="text-xl text-slate-400">
            Deep expertise and tailored AI implementations for your industry
          </p>
        </div>

        {/* Services Accordion */}
        <div className="max-w-5xl mx-auto space-y-4">
          {services.map((service) => {
            const isExpanded = expandedSector === service.id;
            const colors = sectorColors[service.id];

            return (
              <div
                key={service.id}
                className={`bg-slate-900/50 backdrop-blur-sm border ${isExpanded ? colors.border : "border-slate-800"} rounded-2xl overflow-hidden transition-all duration-500 hover:border-${colors.border}`}
              >
                {/* Header */}
                <button
                  onClick={() => toggleSector(service.id)}
                  className="w-full p-6 flex items-center justify-between text-left hover:bg-slate-800/30 transition-colors duration-300"
                >
                  <div className="flex-1">
                    <h3 className={`text-2xl font-bold ${isExpanded ? colors.text : "text-white"} mb-2 transition-colors duration-300`}>
                      {service.sector} Sector
                    </h3>
                    <p className="text-slate-400">{service.title}</p>
                  </div>
                  <div className={`ml-4 ${colors.text}`}>
                    {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </div>
                </button>

                {/* Expanded Content */}
                <div
                  className={`transition-all duration-500 ease-in-out overflow-hidden ${
                    isExpanded ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className={`p-6 pt-0 border-t border-slate-800 bg-gradient-to-b ${colors.gradient}`}>
                    <div className="grid md:grid-cols-2 gap-8">
                      {/* Left Column - Features */}
                      <div>
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center">
                          <span className={`w-2 h-2 ${colors.bg} rounded-full mr-2`}></span>
                          Key Features
                        </h4>
                        <ul className="space-y-3">
                          {service.features.map((feature, index) => (
                            <li key={index} className="flex items-start space-x-3">
                              <CheckCircle2 className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
                              <span className="text-slate-300">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Right Column - Benefits & Description */}
                      <div>
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center">
                          <span className={`w-2 h-2 ${colors.bg} rounded-full mr-2`}></span>
                          Overview
                        </h4>
                        <p className="text-slate-300 mb-6 leading-relaxed">{service.description}</p>

                        <div className={`p-4 ${colors.bg} border ${colors.border} rounded-xl`}>
                          <h5 className="text-sm font-semibold text-white mb-2">Impact</h5>
                          <p className={`${colors.text} font-medium`}>{service.benefits}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SectorServices;